import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME, SVG } from "./attributes";

import { convertFile } from "@/shared/utils/file-helpers";
import { KycUploadFileResponse } from "@/shared/repository/modules/kyc/repo";
import {
  KybParticipantUploadFileResponse,
  KybUploadFileResponse,
} from "@/shared/repository/modules/kyb/repo";
import { UserFileResponse } from "@/shared/repository/modules/user/repo";
import { FileType } from "@/shared/constants/enums";

export default defineComponent({
  name: COMPONENT_NAME,
  props: {
    document: {
      type: <
        PropType<
          | KycUploadFileResponse
          | KybUploadFileResponse
          | KybParticipantUploadFileResponse
          | UserFileResponse
        >
      >Object,
      required: true,
    },
    view: {
      type: String,
      default: "",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    isCenter: {
      type: Boolean,
      default: false,
    },
    isPadding: {
      type: Boolean,
      default: true,
    },
    isDownload: {
      type: Boolean,
      default: false,
    },
    isIcon: {
      type: Boolean,
      default: true,
    },
    isLabel: {
      type: Boolean,
      default: true,
    },
    isError: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: true,
    },
    successIcon: {
      type: String,
      default: "",
    },
    removeView: {
      type: String,
      default: "",
    },
  },
  emits: {
    upload: null,
    remove: null,
  },
  computed: {
    isImage(): boolean {
      if (!this.document) return false;

      const { type, created_at, id } = this.document;

      return (
        type === FileType.image &&
        !!this.displayedFileLink &&
        (!!created_at || !!id)
      );
    },

    isDocument(): boolean {
      if (!this.document) return false;

      const { type, created_at, id } = this.document;

      return (
        type === FileType.document &&
        !!this.displayedFileLink &&
        (!!created_at || !!id)
      );
    },

    isSuccess(): boolean {
      return this.isDocument || this.isImage;
    },

    isImagePreview(): boolean {
      return this.isImage && this.isPreview;
    },

    isIconShow(): boolean {
      return this.isIcon && !this.isImagePreview && !this.isPreloaderShow;
    },

    isLabelShow(): boolean {
      return (
        this.isLabel &&
        !this.isImagePreview &&
        !this.isCenter &&
        !this.isPreloaderShow
      );
    },

    isRemoveShow(): boolean {
      if (this.isReadonly) {
        return false;
      }

      return (
        (this.isImage || this.isDocument) &&
        !this.isDownload &&
        !!this.document.id &&
        !this.isPreloaderShow &&
        !this.isDownload
      );
    },

    isPreloaderShow(): boolean {
      if (!this.document) return false;

      const { isUpload, isRemove } = this.document;

      return isUpload || isRemove;
    },

    isDisabled(): boolean {
      if (!this.document) return this.isLoading;

      const { isUpload, isRemove } = this.document;

      return (
        (this.isLoading ||
          this.isDocument ||
          this.isImage ||
          isUpload ||
          isRemove) &&
        !this.isDownload
      );
    },

    isRemoveDisabled(): boolean {
      if (!this.document) return this.isLoading;

      const { isUpload, isRemove } = this.document;

      return this.isLoading || isUpload || isRemove;
    },

    displayedPreloader(): string {
      return SVG.preloader;
    },

    displayedIcon(): string {
      if (this.isDocument || this.isImage) {
        return this.successIcon || SVG.file;
      }

      return SVG.plus;
    },

    displayedRemoveIcon(): string {
      return SVG.remove;
    },

    displayedFileLink(): string {
      if (!this.document) return "";

      if (this.document.link) return this.document.link;

      return "file" in this.document ? this.document.file : "";
    },

    displayedBackgroundImage(): string {
      if (!this.document) return "";

      return this.isImagePreview && !this.isPreloaderShow
        ? `url(${this.displayedFileLink})`
        : "";
    },
  },
  methods: {
    handleFile(): void {
      if (this.isDownload) {
        this.handleDownload();
      } else {
        (<HTMLInputElement>this.$refs.file).click();
      }
    },

    handleDownload(): void {
      if (!this.document) return;

      const fileLink = document.createElement("a");
      const { link, name } = this.document;

      fileLink.href = link;
      fileLink.download = name;
      fileLink.target = "_blank";

      document.body.appendChild(fileLink);

      fileLink.click();

      document.body.removeChild(fileLink);
    },

    async handleUpload(event: Event): Promise<void> {
      const { files } = <HTMLInputElement>event.target;
      const uploadFile: File | null = files ? files[0] : null;

      if (!uploadFile) return;

      try {
        await convertFile(uploadFile).then((file) => {
          this.$emit("upload", {
            file,
            document: this.document,
          });
        });
      } catch (e) {
        console.log(e);
      } finally {
        (<HTMLInputElement>event.target).value = "";
      }
    },

    handleRemove(): void {
      if (!this.document) return;

      this.$emit("remove", this.document);
    },
  },
});
